"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const pillars = [
{
  icon: "🏆",
  title: "Professional Coaching",
  desc: "Certified coaches across all disciplines. From grassroots to competitive excellence."
},
{
  icon: "🌐",
  title: "World-Class Infrastructure",
  desc: "International-standard courts, pools, and training facilities built for peak performance."
},
{
  icon: "🤝",
  title: "Community-Driven Culture",
  desc: "A vibrant ecosystem where athletes, families, and professionals connect and grow together."
}];


export default function AboutSection() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.15 }
    );

    if (leftRef?.current) observer?.observe(leftRef?.current);
    if (rightRef?.current) observer?.observe(rightRef?.current);

    return () => {
      if (leftRef?.current) observer?.unobserve(leftRef?.current);
      if (rightRef?.current) observer?.unobserve(rightRef?.current);
    };
  }, []);

  return (
    <section
      id="about"
      className="relative py-16 sm:py-20 md:py-28 overflow-hidden"
      style={{ background: "linear-gradient(180deg, var(--navy-dark) 0%, var(--navy-mid) 100%)" }}>
      
      {/* Decorative background */}
      <div
        className="absolute right-0 top-0 w-1/2 h-full pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 80% 50%, rgba(224, 96, 49, 0.05) 0%, transparent 60%)"
        }} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left: Image */}
          <div ref={leftRef} className="reveal-left relative">
            <div className="relative h-[320px] sm:h-[450px] lg:h-[600px] rounded-[24px] overflow-hidden">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_197926ac4-1772077752789.png"
                alt="Athletes training at Play.XP facility with professional coaching and world-class equipment"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(135deg, rgba(27,54,93,0.3) 0%, transparent 60%)"
                }} />
              

              {/* Floating stat card */}
              <div
                className="absolute bottom-8 left-8 glass-card px-6 py-5 max-w-[220px]"
                style={{ backdropFilter: "blur(20px)" }}>
                
                <div className="text-3xl font-bold text-white mb-1">2024</div>
                <div className="text-white/60 text-xs font-semibold uppercase tracking-widest">
                  Founded in Bengaluru
                </div>
                <div className="divider-line mt-3" />
                <div className="text-white/50 text-xs mt-3 leading-relaxed">
                  Built for the next generation of Indian athletes.
                </div>
              </div>

              {/* Terracotta accent bar */}
              <div
                className="absolute top-8 right-8 w-1 h-24 rounded-full"
                style={{ background: "linear-gradient(to bottom, var(--terracotta), transparent)" }} />
              
            </div>
          </div>

          {/* Right: Content */}
          <div ref={rightRef} className="reveal-right space-y-10">
            <div>
              <p className="section-label mb-4">About Play.XP</p>
              <h2 className="headline-lg text-white leading-tight">
                Where Champions{" "}
                <span className="font-display italic" style={{ color: "var(--terracotta)" }}>
                  Begin.
                </span>
              </h2>
            </div>

            <p className="text-white/65 text-lg leading-relaxed">
              Play.XP is Bengaluru's most ambitious multipurpose sports destination — a place where 
              aspiring athletes, seasoned professionals, and active families come together to{" "}
              <strong className="text-white font-semibold">Enjoy. Learn. Compete.</strong>
            </p>

            <p className="text-white/55 leading-relaxed">
              Nestled in Doddajala, North Bengaluru, we've built an ecosystem that goes beyond sport — it's a lifestyle. From championship-grade tennis courts to a recovery café, every detail 
              has been crafted with elite performance in mind.
            </p>

            {/* Terracotta divider */}
            <div
              className="flex items-center gap-4">
              
              <div style={{ width: "40px", height: "2px", background: "var(--terracotta)", borderRadius: "2px" }} />
              <span className="text-white/40 text-xs font-bold uppercase tracking-widest">
                Enjoy. Learn. Compete.
              </span>
            </div>

            {/* Pillars */}
            <div className="space-y-5">
              {pillars?.map((pillar) =>
              <div
                key={pillar?.title}
                className="flex items-start gap-5 p-5 rounded-2xl transition-all duration-300 hover:bg-white/[0.04] group"
                style={{ border: "1px solid rgba(255,255,255,0.05)" }}>
                
                  <span className="text-2xl flex-shrink-0 mt-0.5">{pillar?.icon}</span>
                  <div>
                    <h4 className="text-white font-bold mb-1 group-hover:text-white transition-colors" style={{ color: "rgba(255,255,255,0.9)" }}>
                      {pillar?.title}
                    </h4>
                    <p className="text-white/50 text-sm leading-relaxed">{pillar?.desc}</p>
                  </div>
                </div>
              )}
            </div>

            <a href="#facilities" className="btn-primary inline-flex">
              <span>Explore Facilities</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>);

}